import { useGetChangeOfTenancyMoveWithUs } from "@/api/change-of-tenancy/change-of-tenancy"
import { useGetFeatureFlags } from "@/api/config/config"
import { useGetAccountId } from "@/lib/useGetAccountId"
import ErrorReporter from "@/services/ErrorReporter"
import { computed, ref, watch } from "vue"

import { isMoveWithUsNotFound } from "@/views/myaccount/moving-home/moveWithUsNotFound"

export const useMoveWithUsRecord = () => {
  const featureFlagsQuery = useGetFeatureFlags()
  const isFlagEnabled = computed(
    () => featureFlagsQuery.data.value?.cotStayUpfront === true,
  )

  const { accountId } = useGetAccountId()

  const query = useGetChangeOfTenancyMoveWithUs(accountId, {
    query: {
      enabled: computed(() => isFlagEnabled.value && !!accountId.value),
      throwOnError: (error) => {
        if (!isMoveWithUsNotFound(error)) ErrorReporter.report(error)
        return false
      },
    },
  })

  // A refetch puts a 404'd query back to pending without its error, so keep
  // the last settled answer for the account.
  const settled = ref<{ accountId?: number; error: unknown }>()
  watch([query.isSuccess, query.error], ([isSuccess, error]) => {
    if (isSuccess || error)
      settled.value = { accountId: accountId.value, error }
  })
  const settledError = computed(() =>
    settled.value?.accountId === accountId.value
      ? settled.value.error
      : undefined,
  )

  const record = computed(() =>
    isFlagEnabled.value && query.isSuccess.value ? query.data.value : undefined,
  )

  // Move With Us can only be started with the flag on.
  const hasNoRecord = computed(
    () => !isFlagEnabled.value || isMoveWithUsNotFound(settledError.value),
  )

  const isLoading = computed(() => query.isLoading.value && !settledError.value)

  const isError = computed(
    () => query.isError.value && !isMoveWithUsNotFound(query.error.value),
  )

  return { record, hasNoRecord, isLoading, isError }
}
